import { Layout, PillButton, Text } from 'ds';
import { formatUsd } from 'helpers';
import { Color, ConsumablesOrder, GRAY_50_HEX } from 'shared';
import { selectSalesTaxRate } from 'store/Tenancy/selectors';
import { useAppSelector } from 'store/hooks';
interface CostSummaryBoxProps {
  itemsSubtotalCents: number;
  deliveryFeeCents?: number;
  disclaimerMessage?: JSX.Element;
  sticky?: boolean;
  top?: number;
  changesMade?: boolean;
  onResetChanges?: () => void;
  onClickSaveChanges?: () => void;
  backgroundColor?: Color;
  paddingX?: number;
  footer?: JSX.Element;
  paddingY?: number;
  totalSubtext?: JSX.Element;
  showSkipOrderButton?: boolean;
  onClickSkipOrder?: () => void;
  lockedConsumablesOrder?: ConsumablesOrder;
}

const CostSummaryBox = ({
  itemsSubtotalCents,
  disclaimerMessage,
  sticky = false,
  top,
  deliveryFeeCents,
  changesMade = false,
  onResetChanges,
  onClickSaveChanges,
  backgroundColor,
  paddingX = 24,
  paddingY = 24,
  footer,
  totalSubtext,
  showSkipOrderButton = false,
  onClickSkipOrder,
  lockedConsumablesOrder
}: CostSummaryBoxProps) => {
  const salesTaxRate = useAppSelector(selectSalesTaxRate);
  const nonLockedSalesTaxAmountCents = salesTaxRate
    ? Math.round(((itemsSubtotalCents + (deliveryFeeCents || 0)) * salesTaxRate) / 100)
    : 0;
  const finalSalesTaxAmountCents = lockedConsumablesOrder?.sales_tax_amount_cents || nonLockedSalesTaxAmountCents;
  const finalTotalCents =
    lockedConsumablesOrder?.total_amount_cents ||
    itemsSubtotalCents + (deliveryFeeCents || 0) + (finalSalesTaxAmountCents || 0);

  const finalItemsCents = lockedConsumablesOrder?.items_subtotal_amount_cents || itemsSubtotalCents;

  const finalSubtotalCents =
    lockedConsumablesOrder?.subtotal_amount_cents || itemsSubtotalCents + (deliveryFeeCents || 0);

  const finalDeliveryFeeAmountCents = lockedConsumablesOrder?.delivery_fee_amount_cents || deliveryFeeCents;

  return (
    <Layout
      width="100%"
      paddingTop={paddingY}
      paddingBottom={footer ? 0 : paddingY}
      borderRadius={16}
      color={backgroundColor ? backgroundColor : undefined}
      height="fit-content"
      position={sticky ? 'sticky' : undefined}
      top={sticky ? top : undefined}
      flex
      direction="column"
    >
      <Layout marginBottom={24} paddingX={paddingX}>
        <Text size="headline-sm" color="gray-900">
          Order summary
        </Text>
      </Layout>
      <Layout flex direction="column" gap={16} paddingX={paddingX}>
        <Layout flex direction="row" justify="space-between">
          <Text size="body-sm" semibold>
            Items
          </Text>
          <Text size="body-sm" semibold>
            {formatUsd(finalItemsCents / 100)}
          </Text>
        </Layout>
        {typeof finalDeliveryFeeAmountCents === 'number' && (
          <Layout flex direction="row" justify="space-between">
            <Text size="body-sm" semibold>
              Delivery fee
            </Text>
            <Text size="body-sm" semibold>
              {formatUsd(finalDeliveryFeeAmountCents / 100)}
            </Text>
          </Layout>
        )}
        <Layout flex direction="row" justify="space-between">
          <Text size="body-sm" semibold>
            Restocking
          </Text>
          <Text size="body-sm" color="purple-gradient" semibold>
            Included
          </Text>
        </Layout>
      </Layout>
      <Layout
        flex
        paddingX={paddingX}
        direction="column"
        gap={16}
        __style={{ borderTop: `2px solid ${GRAY_50_HEX}` }}
        paddingTop={24}
        marginTop={24}
      >
        <Layout flex direction="row" justify="space-between">
          <Text size="body-sm" semibold>
            Items subtotal
          </Text>
          <Text size="body-sm" semibold>
            {formatUsd(finalSubtotalCents / 100)}
          </Text>
        </Layout>
        {salesTaxRate && (
          <Layout flex direction="row" justify="space-between">
            <Text size="body-sm" semibold>
              Tax ({salesTaxRate}%)
            </Text>
            <Text size="body-sm" semibold>
              {formatUsd(finalSalesTaxAmountCents / 100)}
            </Text>
          </Layout>
        )}
        <Layout direction="column" gap={4} marginTop={8}>
          <Layout flex direction="row" justify="space-between">
            <Text size="headline-xs" color="gray-900">
              Total
            </Text>
            <Text size="headline-xs" color="gray-900">
              {formatUsd(finalTotalCents / 100)}
            </Text>
          </Layout>
          {totalSubtext && totalSubtext}
        </Layout>
      </Layout>
      {!!disclaimerMessage && (
        <Layout flex direction="row" marginTop={24} justify="space-between" paddingX={paddingX}>
          {disclaimerMessage}
        </Layout>
      )}
      {changesMade ? (
        <Layout flex direction="column" marginTop={24} gap={24} paddingX={paddingX}>
          <Layout direction="column" gap={12}>
            <PillButton borderRadius={40} type="primary" size="md" text="Save changes" onClick={onClickSaveChanges} />
            {showSkipOrderButton && (
              <PillButton
                borderRadius={40}
                type="secondaryOutline"
                size="md"
                text="Skip this order"
                onClick={onClickSkipOrder}
              />
            )}
          </Layout>
          <Layout flex justify="center" align="center">
            <button
              onClick={onResetChanges}
              style={{
                color: '#78909C',
                fontSize: 12,
                fontWeight: 400,
                lineHeight: '16px',
                letterSpacing: '2%',
                width: 'fit-content'
              }}
            >
              Reset changes
            </button>
          </Layout>
        </Layout>
      ) : (
        showSkipOrderButton && (
          <Layout flex direction="column" marginTop={24} gap={24} paddingX={paddingX}>
            <PillButton
              borderRadius={40}
              type="secondaryOutline"
              size="md"
              text="Skip this order"
              onClick={onClickSkipOrder}
            />
          </Layout>
        )
      )}
      {footer && footer}
    </Layout>
  );
};

export default CostSummaryBox;
